import { useEffect, useRef } from 'react'

/**
 * Auto-plays an HTMLMediaElement (typically `<video muted loop playsInline>`)
 * when at least `threshold` of it is visible in the viewport, and pauses it
 * when it scrolls out. Standard social-feed behavior.
 *
 * Pass `enabled: false` to fully disable the observer — useful once the user
 * has taken manual control (e.g., clicked pause) and shouldn't have playback
 * yanked back by intersection events.
 *
 * Autoplay rejection (backgrounded tabs, low-power mode) is silently ignored —
 * this is expected browser behavior, not a bug to surface to error tracking.
 */
export function useIntersectionAutoplay<T extends HTMLMediaElement>(
  options: { threshold?: number; enabled?: boolean } = {}
) {
  const ref = useRef<T | null>(null)
  const threshold = options.threshold ?? 0.5
  const enabled = options.enabled ?? true

  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (!enabled) {
      el.pause()
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {})
        } else {
          el.pause()
        }
      },
      { threshold }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold, enabled])

  return ref
}
